import { XummSdk } from 'xumm-sdk';
import { DEFAULT_CONFIG } from '../types/wallet.js';
import { storePayloadId, clearPayloadId } from './auth.js';

/**
 * XUMM SDK instance
 */
let xummSdk = null;

/**
 * Initialize XUMM SDK
 * @returns {XummSdk|null} XUMM SDK instance or null if failed
 */
export const initializeXumm = () => {
  if (xummSdk) return xummSdk;

  const apiKey = process.env.REACT_APP_XUMM_API_KEY;
  const apiSecret = process.env.REACT_APP_XUMM_API_SECRET;

  if (!apiKey) {
    console.warn('XUMM API Key not configured. Wallet features will be limited.');
    return null;
  }

  try {
    // Initialize with API key only for frontend usage
    // The API secret should NEVER be used in frontend applications
    xummSdk = new XummSdk(apiKey);
    console.log('XUMM SDK initialized successfully');
    return xummSdk;
  } catch (error) {
    console.error('Failed to initialize XUMM SDK:', error);
    return null;
  }
};

/**
 * Create XUMM sign-in payload
 * @param {Object} options - Payload options
 * @returns {Promise<Object>} Payload creation result
 */
export const createSignInPayload = async (options = {}) => {
  const sdk = initializeXumm();
  if (!sdk) {
    throw new Error('XUMM SDK not available');
  }

  try {
    const payload = {
      txjson: {
        TransactionType: 'SignIn'
      },
      options: {
        submit: false,
        multisign: false,
        expire: Math.floor(Date.now() / 1000) + (options.expireMinutes || 10) * 60,
        ...options
      }
    };

    const request = await sdk.payload.create(payload);

    if (!request || !request.uuid) {
      throw new Error('Invalid payload response from XUMM');
    }

    // Store payload ID for tracking
    storePayloadId(request.uuid);

    return {
      success: true,
      uuid: request.uuid,
      qrUrl: request.refs?.qr_png,
      deepLink: request.next?.always,
      websocketUrl: request.refs?.websocket_status,
      payload: request
    };

  } catch (error) {
    console.error('Failed to create XUMM payload:', error);
    throw new Error(`Payload creation failed: ${error.message}`);
  }
};

/**
 * Check XUMM payload status
 * @param {string} payloadId - Payload UUID
 * @returns {Promise<Object>} Payload status result
 */
export const checkPayloadStatus = async (payloadId) => {
  const sdk = initializeXumm();
  if (!sdk) {
    throw new Error('XUMM SDK not available');
  }

  try {
    const status = await sdk.payload.get(payloadId);

    if (!status) {
      throw new Error('Failed to get payload status');
    }

    const result = {
      success: true,
      uuid: payloadId,
      signed: status.meta?.signed || false,
      resolved: status.meta?.resolved || false,
      expired: status.meta?.expired || false,
      cancelled: status.meta?.cancelled || false,
      account: null,
      txid: null,
      status: 'pending'
    };

    if (status.meta?.resolved) {
      if (status.meta?.signed) {
        result.account = status.response?.account;
        result.txid = status.response?.txid;
        result.status = 'resolved';
        clearPayloadId(); // Clear stored payload ID on success
      } else {
        result.status = 'rejected';
        clearPayloadId(); // Clear stored payload ID on rejection
      }
    } else if (status.meta?.expired) {
      result.status = 'expired';
      clearPayloadId(); // Clear stored payload ID on expiration
    } else if (status.meta?.cancelled) {
      result.status = 'cancelled';
      clearPayloadId(); // Clear stored payload ID on cancellation
    }

    return result;

  } catch (error) {
    console.error('Failed to check payload status:', error);
    throw new Error(`Status check failed: ${error.message}`);
  }
};

/**
 * Subscribe to XUMM payload updates
 * @param {string} payloadId - Payload UUID
 * @param {Function} callback - Callback function for updates
 * @returns {Promise<Object>} Subscription object with unsubscribe method
 */
export const subscribeToPayload = async (payloadId, callback) => {
  const sdk = initializeXumm();
  if (!sdk) {
    throw new Error('XUMM SDK not available');
  }

  try {
    const subscription = await sdk.payload.subscribe(payloadId, (event) => {
      const result = {
        uuid: payloadId,
        signed: event.data?.signed || false,
        resolved: event.data?.resolved || false,
        expired: event.data?.expired || false,
        cancelled: event.data?.cancelled || false,
        account: event.data?.account || null,
        txid: event.data?.txid || null
      };

      // Determine status
      if (event.data?.resolved) {
        result.status = event.data?.signed ? 'resolved' : 'rejected';
        clearPayloadId(); // Clear stored payload ID
      } else if (event.data?.expired) {
        result.status = 'expired';
        clearPayloadId();
      } else if (event.data?.cancelled) {
        result.status = 'cancelled';
        clearPayloadId();
      } else {
        result.status = 'pending';
      }

      callback(result);
    });

    return {
      unsubscribe: () => {
        subscription.unsubscribe();
        clearPayloadId();
      }
    };

  } catch (error) {
    console.error('Failed to subscribe to payload:', error);
    throw new Error(`Subscription failed: ${error.message}`);
  }
};

/**
 * Open XUMM wallet with payload
 * @param {string} deepLink - XUMM deep link URL
 * @param {Object} options - Popup options
 * @returns {Window|null} Popup window reference
 */
export const openXummWallet = (deepLink, options = {}) => {
  const popupOptions = {
    width: options.width || DEFAULT_CONFIG.POPUP_DIMENSIONS.width,
    height: options.height || DEFAULT_CONFIG.POPUP_DIMENSIONS.height,
    left: options.left || (window.screen.width / 2) - (DEFAULT_CONFIG.POPUP_DIMENSIONS.width / 2),
    top: options.top || (window.screen.height / 2) - (DEFAULT_CONFIG.POPUP_DIMENSIONS.height / 2),
    ...options
  };

  const popupFeatures = Object.entries(popupOptions)
    .map(([key, value]) => `${key}=${value}`)
    .join(',');

  try {
    const popup = window.open(
      deepLink,
      'xumm_wallet',
      `${popupFeatures},noopener,noreferrer,scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      console.warn('Popup blocked. Please allow popups for this site.');
      return null;
    }

    return popup;

  } catch (error) {
    console.error('Failed to open XUMM wallet:', error);
    return null;
  }
};

/**
 * Create payment payload for XGC tokens
 * @param {string} fromAccount - Sender wallet address
 * @param {string} toAccount - Recipient wallet address
 * @param {string} amount - Amount to send
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Payment payload result
 */
export const createPaymentPayload = async (fromAccount, toAccount, amount, options = {}) => {
  const sdk = initializeXumm();
  if (!sdk) {
    throw new Error('XUMM SDK not available');
  }

  const currencyCode = process.env.REACT_APP_XGC_CURRENCY_CODE || '5847434C00000000000000000000000000000000';
  const issuer = process.env.REACT_APP_XGC_ISSUER;

  if (!issuer) {
    throw new Error('XGC token issuer not configured');
  }

  try {
    const payload = {
      txjson: {
        TransactionType: 'Payment',
        Account: fromAccount,
        Destination: toAccount,
        Amount: {
          currency: currencyCode,
          issuer: issuer,
          value: amount.toString()
        },
        ...options.txjson
      },
      options: {
        submit: true,
        multisign: false,
        expire: Math.floor(Date.now() / 1000) + (options.expireMinutes || 10) * 60,
        ...options.options
      }
    };

    const request = await sdk.payload.create(payload);

    if (!request || !request.uuid) {
      throw new Error('Invalid payment payload response from XUMM');
    }

    storePayloadId(request.uuid);

    return {
      success: true,
      uuid: request.uuid,
      qrUrl: request.refs?.qr_png,
      deepLink: request.next?.always,
      websocketUrl: request.refs?.websocket_status,
      payload: request
    };

  } catch (error) {
    console.error('Failed to create payment payload:', error);
    throw new Error(`Payment payload creation failed: ${error.message}`);
  }
};

/**
 * Create trust line payload for XGC tokens
 * @param {string} account - Wallet address
 * @param {string} limit - Trust line limit (optional)
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Trust line payload result
 */
export const createTrustLinePayload = async (account, limit = '1000000000', options = {}) => {
  const sdk = initializeXumm();
  if (!sdk) {
    throw new Error('XUMM SDK not available');
  }

  const currencyCode = process.env.REACT_APP_XGC_CURRENCY_CODE || '5847434C00000000000000000000000000000000';
  const issuer = process.env.REACT_APP_XGC_ISSUER;

  if (!issuer) {
    throw new Error('XGC token issuer not configured');
  }

  try {
    const payload = {
      txjson: {
        TransactionType: 'TrustSet',
        Account: account,
        LimitAmount: {
          currency: currencyCode,
          issuer: issuer,
          value: limit
        },
        ...options.txjson
      },
      options: {
        submit: true,
        multisign: false,
        expire: Math.floor(Date.now() / 1000) + (options.expireMinutes || 10) * 60,
        ...options.options
      }
    };

    const request = await sdk.payload.create(payload);

    if (!request || !request.uuid) {
      throw new Error('Invalid trust line payload response from XUMM');
    }

    storePayloadId(request.uuid);

    return {
      success: true,
      uuid: request.uuid,
      qrUrl: request.refs?.qr_png,
      deepLink: request.next?.always,
      websocketUrl: request.refs?.websocket_status,
      payload: request
    };

  } catch (error) {
    console.error('Failed to create trust line payload:', error);
    throw new Error(`Trust line payload creation failed: ${error.message}`);
  }
};

/**
 * Cancel XUMM payload
 * @param {string} payloadId - Payload UUID
 * @returns {Promise<boolean>} Success status
 */
export const cancelPayload = async (payloadId) => {
  const sdk = initializeXumm();
  if (!sdk) {
    return false;
  }

  try {
    await sdk.payload.cancel(payloadId);
    clearPayloadId();
    return true;
  } catch (error) {
    console.error('Failed to cancel payload:', error);
    return false;
  }
};

/**
 * Get XUMM app info
 * @returns {Promise<Object>} App info
 */
export const getXummAppInfo = async () => {
  const sdk = initializeXumm();
  if (!sdk) {
    throw new Error('XUMM SDK not available');
  }

  try {
    const appInfo = await sdk.ping();
    return {
      success: true,
      info: appInfo
    };
  } catch (error) {
    console.error('Failed to get XUMM app info:', error);
    throw new Error(`App info request failed: ${error.message}`);
  }
}; 